import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLogo } from 'src/components/generic-components/AppLogo';
import { PARENT_TO_EMBED_MESSAGES } from 'src/constants/EmbedMessageConstants';
import type { EmbeddedAppState } from 'src/hooks/useEmbeddedApp';
import ARROW_ICON from '../../../images/arrow.svg';
import BASEBALL_CARDS_LOGO from '../../../images/baseballcards_logo.svg';
import CANARY_LOGO from '../../../images/canary_logo.svg';
import CAPACITY_PLANNER_LOGO from '../../../images/airspace.svg';
import DFP_LOGO_TEXT from '../../../images/finsuite-text.svg';
import HUDDLE_PLUS_LOGO from '../../../images/huddle-plus-logo.svg';
import KINGPIN_LOGO from '../../../images/frame_logo.svg';
import SIGNAL_LOGO from '../../../images/signal.svg';
import { LOGO_PALETTES, LogoIcon } from './LogoIcon';

/**
 * Maps logo'd route paths to their SVG logos. Only mapped routes show logo +
 * name; unmapped routes show name only. Covers both org-tool routes and the
 * Kingpin section.
 */
const APP_LOGOS: Record<string, string> = {
  '/ea-huddle': HUDDLE_PLUS_LOGO,
  '/admin/ea-huddle': HUDDLE_PLUS_LOGO,
  '/baseball-cards': BASEBALL_CARDS_LOGO,
  '/canary': CANARY_LOGO,
  '/kingpin': KINGPIN_LOGO,
  '/kingpin/goals': KINGPIN_LOGO,
  '/kingpin/insights': KINGPIN_LOGO,
  '/kingpin/charts': KINGPIN_LOGO,
  '/capacity-planner/my-week': CAPACITY_PLANNER_LOGO,
  '/capacity-planner/grid': CAPACITY_PLANNER_LOGO,
  '/capacity-planner/trends': CAPACITY_PLANNER_LOGO,
  '/capacity-planner/by-project': CAPACITY_PLANNER_LOGO,
  '/capacity-planner/by-team': CAPACITY_PLANNER_LOGO,
  '/capacity-planner/commentary': CAPACITY_PLANNER_LOGO,
  '/capacity-planner/exceptions': CAPACITY_PLANNER_LOGO,
  '/capacity-planner/setup': CAPACITY_PLANNER_LOGO,
  '/signal': SIGNAL_LOGO,
  '/signal/problems': SIGNAL_LOGO,
  '/signal/list': SIGNAL_LOGO,
  '/signal/prioritization': SIGNAL_LOGO,
  '/signal/heatmap': SIGNAL_LOGO,
  '/signal/admin': SIGNAL_LOGO
};

/** Sections whose routes should render the embedded-style logo + name in the top nav. */
const LOGO_SECTIONS = new Set(['org-tools', 'kingpin']);

/**
 * TopNavLogo receives all embedded app state from the parent DFPTopNavigation
 * to avoid duplicate hook instances and divergent readiness state.
 */
export const TopNavLogo: React.FC<EmbeddedAppState> = ({
  embeddedApp,
  embedMatch,
  sendMessageToEmbed,
  displayAppName,
  routeConfig,
  embedKey,
  embedPath
}) => {
  const navigate = useNavigate();

  // Canonical path to the current embedded app. Prefer the hook-provided
  // embedPath (`/{urlName}` or `/all-apps/{id}`); fall back to the legacy
  // embedMatch id for consumers/tests that don't supply it.
  const embedHomePath = embedPath ?? (embedMatch?.params?.id ? `/all-apps/${embedMatch.params.id}` : null);

  const handleEmbeddedAppHomeNavigation = useCallback(() => {
    if (sendMessageToEmbed(PARENT_TO_EMBED_MESSAGES.NAVIGATE_HOME)) return;
    if (embedHomePath) {
      navigate(`${embedHomePath}?t=${Date.now()}`);
    }
  }, [embedHomePath, navigate, sendMessageToEmbed]);

  const embedIdentity = embedKey ?? embedMatch?.params?.id ?? null;
  const isEmbedded = !!embeddedApp && !!embedIdentity;
  const isOrgToolRoute = !isEmbedded && !!displayAppName && !!routeConfig?.navigationSection && LOGO_SECTIONS.has(routeConfig.navigationSection);
  const showEmbeddedStyle = isEmbedded || isOrgToolRoute;
  const currentEmbedId = embedIdentity;
  // Key the "currently active app" by navigation section when we're inside
  // a logo'd section (kingpin / org-tools). Sub-tabs of the same section
  // share the same key, so navigating between e.g. /kingpin/goals and
  // /kingpin/charts doesn't replay the logo intro animation.
  const currentRouteKey = isOrgToolRoute ? routeConfig?.navigationSection ?? routeConfig?.path ?? null : currentEmbedId;

  const isInitialMount = useRef(true);
  const prevEmbedded = useRef(showEmbeddedStyle);
  const prevEmbedId = useRef(currentRouteKey);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [textHidden, setTextHidden] = useState(showEmbeddedStyle);
  const [logoFrame, setLogoFrame] = useState(showEmbeddedStyle ? LOGO_PALETTES.length - 1 : 0);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevEmbedId.current = currentRouteKey;
      return;
    }

    // Detect app-to-app switch (showEmbeddedStyle stays true but ID changes)
    const isAppSwitch = showEmbeddedStyle && prevEmbedded.current && currentRouteKey !== prevEmbedId.current;
    prevEmbedId.current = currentRouteKey;

    if (!showEmbeddedStyle) {
      setShouldAnimate(false);
      if (prevEmbedded.current) {
        setTextHidden(true);
        setLogoFrame(LOGO_PALETTES.length - 1);
        let innerRaf: number;
        const raf = requestAnimationFrame(() => {
          innerRaf = requestAnimationFrame(() => {
            setTextHidden(false);
            setLogoFrame(0);
          });
        });
        prevEmbedded.current = false;
        return () => {
          cancelAnimationFrame(raf);
          cancelAnimationFrame(innerRaf);
        };
      }
      setTextHidden(false);
      setLogoFrame(0);
      prevEmbedded.current = false;
      return;
    }

    prevEmbedded.current = true;

    if (isAppSwitch) {
      // Reset animation state first, then re-trigger on next frame
      setShouldAnimate(false);
      const raf = requestAnimationFrame(() => {
        setShouldAnimate(true);
      });
      return () => cancelAnimationFrame(raf);
    }

    setShouldAnimate(true);
    const textRaf = requestAnimationFrame(() => setTextHidden(true));

    let frame = 0;
    let interval: ReturnType<typeof setInterval>;
    const startDelay = setTimeout(() => {
      interval = setInterval(() => {
        frame++;
        if (frame >= LOGO_PALETTES.length - 1) {
          setLogoFrame(LOGO_PALETTES.length - 1);
          clearInterval(interval);
        } else {
          setLogoFrame(frame);
        }
      }, 60);
    }, 300);

    return () => {
      cancelAnimationFrame(textRaf);
      clearTimeout(startDelay);
      clearInterval(interval);
    };
  }, [showEmbeddedStyle, currentRouteKey]);

  const [isLogoHovered, setIsLogoHovered] = useState(false);

  return (
    <div className={`dfp-top-nav__left${showEmbeddedStyle ? ' dfp-top-nav__left--embedded' : ''}`}>
      <a
        href="/"
        className="dfp-top-nav__logo-link"
        onClick={(e) => {
          e.preventDefault();
          navigate('/');
        }}
        onMouseEnter={() => setIsLogoHovered(true)}
        onMouseLeave={() => setIsLogoHovered(false)}
        aria-label="FinSuite Home"
      >
        <LogoIcon paletteIndex={isLogoHovered ? 0 : logoFrame} className="dfp-top-nav__logo-icon" />
        <img src={DFP_LOGO_TEXT} alt="FinSuite" className={`dfp-top-nav__logo-text${textHidden ? ' dfp-top-nav__logo-text--hidden' : ''}`} />
      </a>
      {showEmbeddedStyle && displayAppName && (
        <img
          key={`arrow-${currentRouteKey}`}
          src={ARROW_ICON}
          alt=""
          className={`dfp-top-nav__arrow${shouldAnimate ? ' dfp-top-nav__arrow--slide-in' : ''}`}
        />
      )}
      {showEmbeddedStyle && displayAppName && (
        <a
          key={`app-${currentRouteKey}`}
          href={isEmbedded ? embedHomePath || '#' : routeConfig?.path || '#'}
          className={`dfp-top-nav__app_text${shouldAnimate ? ' dfp-top-nav__app_text--animated' : ''}`}
          onClick={(e) => {
            if (isEmbedded) {
              e.preventDefault();
              handleEmbeddedAppHomeNavigation();
            } else if (routeConfig?.path === '/ea-huddle') {
              // EA Huddle needs full page reload to reset hook state
              return;
            } else {
              e.preventDefault();
              if (routeConfig?.navigationSection === 'kingpin') {
                navigate('/kingpin/goals');
              } else if (routeConfig?.path) {
                navigate(routeConfig.path);
              }
            }
          }}
          aria-label={`Navigate to ${isEmbedded ? embeddedApp!.name : routeConfig?.navigationText} home`}
        >
          {isEmbedded && embeddedApp?.logoConfig && (embeddedApp.logoConfig.selectedLogoName || embeddedApp.logoConfig.s3Location) && (
            <span className="dfp-top-nav__app-logo-wrapper">
              <AppLogo logoConfig={embeddedApp.logoConfig} />
            </span>
          )}
          {isOrgToolRoute && routeConfig?.path && APP_LOGOS[routeConfig.path] && (
            <span className={`dfp-top-nav__app-logo-wrapper${routeConfig.path === '/canary' ? ' dfp-top-nav__app-logo-wrapper--lg' : ''}`}>
              <img
                src={APP_LOGOS[routeConfig.path]}
                alt=""
                className={`dfp-top-nav__app-logo-img${routeConfig.path === '/canary' ? ' dfp-top-nav__app-logo-img--lg' : ''}`}
              />
            </span>
          )}
          <span className="dfp-top-nav__app-name">{displayAppName}</span>
        </a>
      )}
    </div>
  );
};
